import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataProvider, useData } from "../../context/DataContext.jsx";
import { jobAccess } from "../jobs/lib/jobAccess.js";
import { C, font, mono } from "../../lib/styles.js";
import { BLANK_SEGMENT } from "../../lib/utils.js";
import { normalizeRules } from "../../lib/segments.js";
import ContactsView from "./ContactsView.jsx";
import ClientsView from "./ClientsView.jsx";
import SegmentsView from "./SegmentsView.jsx";
import CampaignsView from "./CampaignsView.jsx";
import SearchPalette, { SEARCH_EVENT, SearchIcon } from "./SearchPalette.jsx";
import { useAllInteractions } from "./useAllInteractions.js";
import { getApp } from "../../lib/appRegistry.js";
import { usePageIds } from "../../lib/feedback/pageIds.js";

/* The topbar hands this app a tab INDEX, and the CRM's tab list grows one
   entry per slice (Segments in 4, Content in 6). Looking the label up in the
   registry means a tab inserted before Campaigns moves Campaigns with it,
   instead of silently routing the new tab to the old screen. */
const crmNav = () => getApp("crm")?.navItems || ["Contacts", "Clients"];
const tabIndex = (label) => crmNav().indexOf(label);

/* CRM app shell — the suite-io home for the Contacts + Clients screens ported
   from nyc-suite legacy (2026-08-26), so the old app can retire. Plays the role
   legacy ContactsPanel played: owns the cross-view navigation state (which
   detail is open, where the user came from) and feeds both views from
   useData() — the views themselves stay props-based, unchanged from legacy.

   Not ported (still legacy-only, by scope decision): Inbox, Campaigns,
   Assistant, Tasks, and the Gmail-backed Reinform feature. The CRM still has
   nowhere to send a job or quote row, so those render read-only. */
/* The CRM views read contacts, clients, jobs and quotes through useData(), so
   the app needs the same role-keyed DataProvider that JobsApp mounts. Until
   2026-09-04 nothing mounted one here, and opening the CRM tab crashed with
   "useData must be used within DataProvider" on every deploy since the
   2026-08-26 port. Non-admin roles get an empty CRM by design: the provider
   never requests contacts or clients for them. */
export default function CrmApp(props) {
  const role = jobAccess(props.user?.role).role;
  return (
    <DataProvider key={role} role={role}>
      <CrmAppInner {...props} />
    </DataProvider>
  );
}

/* Exported for the ?e2e=crm fixture, which supplies its own seeded provider. */
export function CrmAppInner({ activeTab, setActiveTab, user }) {
  const {
    contacts = [],
    clients = [],
    jobs = [],
    savedQuotes = [],
    holds = [],
    segments = [],
    campaigns = [],
    saveContact,
    saveClient,
    saveSegment,
    removeContact,
    removeClient,
    removeSegment,
    saveCampaign,
    reloadCampaigns,
    role = "viewer",
  } = useData();

  const [activeContact, setActiveContact] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  /* The unsaved segment "Save as segment" hands to the Segments tab. It lives
     here, not in SegmentsView, because it is written on one tab and read on
     another. */
  const [newSegment, setNewSegment] = useState(null);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  // The bar at the top of both tabs and the ⌘K palette share one query: typing
  // in the bar opens the palette carrying what has been typed so far.
  const [barQuery, setBarQuery] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [rowNotice, setRowNotice] = useState(null);
  // For a feedback report from the CRM: the record open, by id only.
  const idOf = (v) => (typeof v === "string" ? v : v?.id);
  usePageIds("crm", {
    contactId: idOf(activeContact),
    clientId: idOf(activeClient),
    segmentId: idOf(activeSegmentId),
    campaignId: idOf(activeCampaign),
  });

  // Same matchMedia pattern as the rest of the suite (never window.innerWidth).
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Cross-view navigation, ported from legacy ContactsPanel: opening a client
  // from a contact card (or vice versa) switches tabs and remembers the origin
  // so the back button returns there.
  /* Memoized: both views bind a window Escape handler whose dependency array
     names their close callback, and an identity that changed on every render
     would rebind that listener on every render. */
  const openClientFrom = useCallback((client, fromTab) => {
    setReturnTo(fromTab);
    setActiveClient(client);
    setActiveContact(null);
    setActiveTab(1);
  }, [setActiveTab]);
  const openContactFrom = useCallback((contact, fromTab) => {
    setReturnTo(fromTab);
    setActiveContact(contact);
    setActiveClient(null);
    setActiveTab(0);
  }, [setActiveTab]);
  const navigateBack = useCallback(() => {
    if (returnTo === "contacts") setActiveTab(0);
    if (returnTo === "clients") setActiveTab(1);
    setActiveContact(null);
    setActiveClient(null);
    setReturnTo(null);
  }, [returnTo, setActiveTab]);

  /* Save as segment, from either list's filter row: the filters become rules
     and the Segments tab opens on an unsaved record waiting for a name. */
  const saveAsSegment = useCallback((rules) => {
    setNewSegment({ ...BLANK_SEGMENT(), createdBy: user?.email || "", rules: normalizeRules(rules) });
    setActiveSegmentId(null);
    setActiveContact(null);
    setActiveClient(null);
    // By label, not by 2: appRegistry owns the order, and Segments moves the
    // day a tab lands in front of it.
    setActiveTab(tabIndex("Segments"));
  }, [setActiveTab, user]);

  // Switching tabs from the topbar clears any open detail so each tab lands on
  // its list — mirrors legacy behavior of the top-level CRM tabs.
  const [lastTab, setLastTab] = useState(activeTab);
  if (activeTab !== lastTab) {
    setLastTab(activeTab);
    if (!returnTo) { setActiveContact(null); setActiveClient(null); }
    setActiveCampaign(null);
  }

  /* ⌘K / Ctrl+K anywhere in the CRM, plus the topbar badge, which lives in a
     sibling component (App.jsx renders AppTopbar next to this app) and so asks
     through a window event rather than a prop drilled up and back down. */
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(SEARCH_EVENT, openPalette);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(SEARCH_EVENT, openPalette);
    };
  }, [openPalette]);

  /* A job or quote row has nowhere to navigate to until the suite shell grows
     cross-app navigation, so it says what it found and stops — same posture as
     the read-only job rows inside the drawers. */
  useEffect(() => {
    if (!rowNotice) return undefined;
    const t = setTimeout(() => setRowNotice(null), 6000);
    return () => clearTimeout(t);
  }, [rowNotice]);

  const collections = useMemo(
    () => ({ contacts, clients, jobs, savedQuotes }),
    [contacts, clients, jobs, savedQuotes],
  );

  /* The Campaigns tab needs the whole interactions collection: the check-ins
     row counts /crm hello records, and a sent campaign's replies are inbound
     mail from its recipients. Same lazy, session-cached, admin-only hook the
     ⌘K palette uses — never a second getDocs. */
  const allInteractions = useAllInteractions(role);
  const onCampaignsTab = activeTab === tabIndex("Campaigns");
  useEffect(() => { if (onCampaignsTab) allInteractions.load(); }, [onCampaignsTab, allInteractions]);
  const userEmail = user?.email || "";

  /* Opening from the palette reuses the cross-view navigation the drawers
     already have. It must NOT set the detail and the tab separately: switching
     tabs with no `returnTo` clears whatever detail is open (the block above),
     so a contact opened from the Clients tab would be wiped on the same
     render. Going through openContactFrom / openClientFrom sets `returnTo`
     first, which both prevents that and gives the drawer a working back
     button to the tab the search was run from. */
  /* Memoized for the same reason as the navigation above: SearchPalette binds
     one window key handler per opening, and its dependency array names these. */
  const fromTab = activeTab === 1 ? "clients" : "contacts";
  const openSearchContact = useCallback(
    (record) => openContactFrom(contacts.find(c => c.id === record?.id) || record, fromTab),
    [contacts, openContactFrom, fromTab],
  );
  const openSearchClient = useCallback(
    (record) => openClientFrom(clients.find(c => c.id === record?.id) || record, fromTab),
    [clients, openClientFrom, fromTab],
  );
  const openSearchOther = useCallback((result) => setRowNotice(result), []);

  const searchBar = (
    <div style={sb.wrap} data-testid="crm-search-bar">
      <SearchIcon size={16} />
      <input
        style={sb.input}
        value={barQuery}
        placeholder="Search contacts, clients, jobs, quotes, emails"
        aria-label="Search"
        data-testid="crm-search-bar-input"
        /* onChange ONLY. There used to be an onFocus that reopened the
           palette whenever the bar held text, and it made Escape impossible:
           closing the palette hands focus back to this input (so the query
           stays editable), the focus handler saw a non-empty query and
           reopened within the same frame. Reopening has to be a deliberate
           act — type another character, click the badge, or press ⌘K. */
        onChange={(e) => { setBarQuery(e.target.value); setPaletteOpen(true); }}
      />
      {!isMobile && (
        <span style={sb.hint}>a name, an email, a phone, a domain, a quote handle, a word from a message</span>
      )}
      <button type="button" style={sb.kbd} onClick={openPalette} aria-label="Open search">⌘K</button>
    </div>
  );

  const chrome = (view) => (
    <div>
      {/* The drawer now opens BESIDE the table (variant A), so the bar stays
          where it is on a desktop. On a phone the drawer takes the screen and
          the bar would be searching a list that is not on it. */}
      {(!isMobile || (!activeContact && !activeClient)) && searchBar}
      {rowNotice && (
        <div style={sb.notice} data-testid="crm-search-notice">
          {NOTICE_NOUN[rowNotice.kind] || "Record"} {rowNotice.title}
          {rowNotice.subtitle ? ` · ${rowNotice.subtitle}` : ""} — {rowNotice.kind === "contact"
            ? "open it on the Contacts tab; the CRM cannot navigate there from here yet."
            : "open it in the Jobs app; the CRM cannot navigate there yet."}
        </div>
      )}
      {view}
      <SearchPalette
        open={paletteOpen}
        onClose={closePalette}
        initialQuery={barQuery}
        collections={collections}
        role={role}
        isMobile={isMobile}
        onOpenContact={openSearchContact}
        onOpenClient={openSearchClient}
        onOpenOther={openSearchOther}
      />
    </div>
  );

  if (activeTab === tabIndex("Segments")) {
    return chrome(
      <SegmentsView
        segments={segments}
        contacts={contacts}
        clients={clients}
        jobs={jobs}
        savedQuotes={savedQuotes}
        campaigns={campaigns}
        user={user}
        saveSegment={saveSegment}
        removeSegment={removeSegment}
        activeSegmentId={activeSegmentId}
        setActiveSegmentId={setActiveSegmentId}
        newSegment={newSegment}
        setNewSegment={setNewSegment}
        campaignsTab={tabIndex("Campaigns")}
        /* "Use in a campaign" lands on the Campaigns tab. Campaigns are
           written from the chat (plan: the chat drafts, the Suite approves),
           so the tab is where the draft for this audience will appear;
           starting one from inside the Suite is a later slice. */
        onOpenCampaign={() => setActiveTab(tabIndex("Campaigns"))}
        isMobile={isMobile}
      />,
    );
  }

  if (activeTab === tabIndex("Campaigns")) {
    return chrome(
      <CampaignsView
        campaigns={campaigns}
        segments={segments}
        contacts={contacts}
        clients={clients}
        jobs={jobs}
        savedQuotes={savedQuotes}
        interactions={allInteractions.records}
        saveCampaign={saveCampaign}
        reloadCampaigns={reloadCampaigns}
        activeCampaign={activeCampaign}
        setActiveCampaign={setActiveCampaign}
        onEvidence={setRowNotice}
        userEmail={userEmail}
        isMobile={isMobile}
        paletteOpen={paletteOpen}
      />,
    );
  }

  if (activeTab === 1) {
    return chrome(
      <ClientsView
        clients={clients}
        contacts={contacts}
        jobs={jobs}
        savedQuotes={savedQuotes}
        holds={holds}
        role={role}
        saveClient={saveClient}
        removeClient={removeClient}
        openContactFrom={openContactFrom}
        activeClient={activeClient}
        setActiveClient={setActiveClient}
        navigateBack={navigateBack}
        returnTo={returnTo}
        isMobile={isMobile}
        paletteOpen={paletteOpen}
        onSaveAsSegment={saveAsSegment}
      />,
    );
  }

  return chrome(
    <ContactsView
      contacts={contacts}
      clients={clients}
      jobs={jobs}
      savedQuotes={savedQuotes}
      saveContact={saveContact}
      removeContact={removeContact}
      openClientFrom={openClientFrom}
      activeContact={activeContact}
      setActiveContact={setActiveContact}
      navigateBack={navigateBack}
      returnTo={returnTo}
      isMobile={isMobile}
      paletteOpen={paletteOpen}
      onSaveAsSegment={saveAsSegment}
    />,
  );
}

/* The 46px bar from the mockup, above whichever tab is showing. It is a door
   into the palette, not a filter: typing in it opens the palette carrying the
   query. The per-list filter box inside each view stays until slice 3
   rebuilds those screens. */
const NOTICE_NOUN = { quote: "Quote", job: "Job", contact: "Contact" };

const sb = {
  wrap: {
    display: "flex", alignItems: "center", gap: 12, background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 8, padding: "0 14px",
    height: 46, marginBottom: 18, fontFamily: font,
  },
  input: {
    flex: 1, border: 0, outline: 0, background: "transparent",
    font: `300 14.5px ${font}`, color: C.text, minWidth: 0,
  },
  hint: { fontSize: 11.5, color: C.muted, whiteSpace: "nowrap" },
  kbd: {
    fontFamily: mono, fontSize: 10, color: C.muted, border: `1px solid ${C.border}`,
    borderRadius: 4, padding: "2px 6px", background: C.surface, marginLeft: 4,
  },
  notice: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "9px 12px", marginBottom: 14, fontSize: 12.5, color: C.muted, fontFamily: font,
  },
};
